import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { of } from "rxjs";

import { IAppState } from "@edp-store/state/app.state";
import { Store } from "@ngrx/store";
import {
    EEnergyEfficiencyActions,
    GetComparatorEfficientHouse,
    GetComparatorElectricMobility,
    GetContentCardData,
    GetContractEfficiency,
    GetProductServiceCardData,
    SetComparatorEfficientHouse,
    SetComparatorElectricMobility,
    SetContentCardData,
    SetContractEfficiency,
    SetPsData as SetProductServiceData
} from "@edp-store/actions/energy-efficiency.actions";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { getUserEfficiency } from "@edp-store/selectors/edpe.selector";
import { EdpeService } from "@edp-services/edpe.service";
import {
    ComparatorEfficientHouse,
    ComparatorElectricMobility,
    ContractEfficiency,
    ContractPsData,
    ContractPsServiceData,
    ProductServiceCardsFactory,
    ProductServiceCode,
    UserEfficiencyData
} from "@edp-models/energy-efficiency.model";
import { Card } from "@edp-models/benefits.model";
import { EnvService } from "@edp-services/env.service";

@Injectable()
export class EnergyEfficiencyEffects {

    @Effect()
    loadContractEfficiency$ = this.actions$.pipe(
        ofType<GetContractEfficiency>(EEnergyEfficiencyActions.GetContractEfficiency),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        withLatestFrom(this.store.select(getUserEfficiency)),
        switchMap(([action, userEfficiency]) => {
            if (userEfficiency && action?.contract) {
                const contractEfficiency = userEfficiency.contracts
                    .find((contract) => contract.contractId === action.contract.ContractId);
                return of(new SetContractEfficiency(new ContractEfficiency(contractEfficiency)), new ShowLoader(false));
            }
            return of(new SetContractEfficiency(undefined), new ShowLoader(false));
        })
    );

    @Effect()
    getProductServiceCards = this.actions$.pipe(
        ofType(EEnergyEfficiencyActions.GetProductServiceCardData),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        switchMap((action: GetProductServiceCardData) => {
            const initialCards = ProductServiceCardsFactory.generateInitialCards();
            return this.edpeService.getProductServiceCardData(
                initialCards.filter(card => card.code !== ProductServiceCode.NONE).map(card => ProductServiceCode[card.code])
            );
        }),
        mergeMap((serviceData: ContractPsServiceData[]) => {
            const psData: ContractPsData[] = [];

            serviceData.forEach((ps) => {
                const contractCards = ProductServiceCardsFactory.generateInitialCards()
                    .filter(card => card.code !== ProductServiceCode.NONE);
                contractCards.forEach((card) => {
                    card.serviceData = ps.cardData.find(data => card.code && ProductServiceCode[card.code] === data.code);
                });
                psData.push({contractId: ps.contractId, cards: contractCards});
            });

            return of(new SetProductServiceData(psData), new ShowLoader(false));
        })
    );

    @Effect()
    getContentCards = this.actions$.pipe(
        ofType(EEnergyEfficiencyActions.GetContentCardData),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        switchMap((action: GetContentCardData) => {
            return this.edpeService.getEnergyEfficiencyCards(action.contract.ContractId);
        }),
        mergeMap((serviceData: UserEfficiencyData[]) => {
            let cards: Card[] = [];

            serviceData.forEach( data => {
                cards = data.cards;
            });

            cards.forEach(card => {
                card.image = this.getImage(card.image);
                card.imageMobile = this.getImage(card.imageMobile);
                card.location = "dashboard";
            });

            return of(new SetContentCardData(cards), new ShowLoader(false));
        })
    );

    @Effect()
    getComparatorEfficientHouse = this.actions$.pipe(
        ofType(EEnergyEfficiencyActions.GetComparatorEfficientHouse),
        switchMap((action: GetComparatorEfficientHouse) => {
            return this.edpeService.getComparatorEfficientHouse(action.contract.ContractId)
            .pipe(catchError((error: Error) => of(null)));
        }),
        mergeMap((data: ComparatorEfficientHouse) => {
            return of(new SetComparatorEfficientHouse(data));
        })
    );

    @Effect()
    getComparatorElectricMobility = this.actions$.pipe(
        ofType(EEnergyEfficiencyActions.GetComparatorElectricMobility),
        switchMap((action: GetComparatorElectricMobility) => {
            return this.edpeService.getComparatorElectricMobility(action.contract.ContractId)
            .pipe(catchError((error: Error) => of(null)));
        }),
        mergeMap((data: ComparatorElectricMobility) => {
            return of(new SetComparatorElectricMobility(data));
        })
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store<IAppState>,
        private edpeService: EdpeService,
        private envService: EnvService,
    ) { }

    private getImage(img: string) {
        if (!img || img.indexOf("resource") >= 0) {
            return `${this.envService.appUrl()}${img}`;
        } else {
            return `${this.envService.appUrl()}/resource/image/${img}`;
        }
    }

}
